import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn.js';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerOutline';
type Size = 'sm' | 'md';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-action text-on-action hover:bg-action-hover border border-action hover:border-action-hover',
  secondary: 'bg-surface text-ink border border-line hover:bg-surface-2 hover:border-gray-400',
  ghost: 'bg-transparent text-ink-2 border border-transparent hover:bg-surface-2 hover:text-ink',
  dangerOutline:
    'bg-surface text-danger-700 border border-danger-100 hover:bg-danger-50 hover:border-danger-600',
  danger: 'bg-danger-600 text-on-accent border border-danger-600 hover:bg-danger-700',
};
const SIZES: Record<Size, string> = { sm: 'h-8 px-3 text-[13px] gap-1.5', md: 'h-10 px-4 text-sm gap-2' };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
}

/** Bouton en casse de phrase, rayon 4 px. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-sm font-display font-semibold',
        'transition-colors duration-base ease-od active:translate-y-px',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:active:translate-y-0',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size?: Size;
  active?: boolean;
}

/** Bouton d'icône seule, avec libellé accessible obligatoire. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', active, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-sm border transition-colors duration-base ease-od',
        'disabled:cursor-not-allowed disabled:opacity-45',
        size === 'sm' ? 'h-8 w-8' : 'h-10 w-10',
        active
          ? 'border-navy-600 bg-navy-50 text-navy-600'
          : 'border-transparent text-ink-2 hover:bg-surface-2 hover:text-ink',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
